import * as React from 'react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaperAirplaneIcon, SparklesIcon, XMarkIcon, ArrowPathIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { KeycloakContext } from '../../App';
import { useFeatures } from '../../providers/FeaturesProvider';
import { API_URL } from '../../runtimeConfig';
import { cn } from '../../lib/cn';

const DASHBOARD_PATHS = [
  '/services', '/domains', '/websites', '/containers', '/vps',
  '/emails', '/databases', '/messages', '/orders', '/bills',
  '/support', '/status', '/profile',
];

function renderMessageContent(text: string, navigate: (path: string) => void) {
  const regex = /(\(\s*(\/[a-z]+)\s*\))/gi;
  const parts: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const fullMatch = match[1];
    const path = match[2];
    if (!DASHBOARD_PATHS.includes(path)) continue;
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <a
        key={match.index}
        href={path}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(path); }}
        className="text-primary underline underline-offset-2 font-medium hover:text-primary/80"
      >
        {fullMatch}
      </a>
    );
    lastIndex = match.index + fullMatch.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? parts : text;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [aiEnabled, setAiEnabled] = useState(true);
  const { isEnabled } = useFeatures();
  const navigate = useNavigate();
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;

  const scrollToBottom = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { if (isOpen) inputRef.current?.focus(); }, [isOpen]);

  const fetchSuggestions = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/ai/suggestions`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setAiEnabled(true);
      } else if (res.status === 503) {
        setAiEnabled(false);
      }
    } catch {
      /* noop */
    }
  }, [keycloak?.token]);

  useEffect(() => {
    if (!keycloak?.token) return;
    let active = true;
    fetch(`${API_URL}/ai/status`, { headers: { Authorization: `Bearer ${keycloak.token}` } })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => {
        if (active) setAiEnabled(data?.enabled !== false);
      })
      .catch(() => {
        if (active) setAiEnabled(false);
      });
    return () => {
      active = false;
    };
  }, [keycloak?.token]);

  useEffect(() => {
    if (isOpen && messages.length === 0) fetchSuggestions();
  }, [isOpen, messages.length, fetchSuggestions]);

  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;
    const userMessage: ChatMessage = { role: 'user', content: messageText.trim() };
    setInput('');
    setIsLoading(true);
    setIsThinking(true);
    setSuggestions([]);
    const history = [...messages, userMessage].map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, userMessage, { role: 'assistant', content: '' }]);
    try {
      const res = await fetch(`${API_URL}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${keycloak?.token}` },
        body: JSON.stringify({ message: messageText.trim(), conversation_history: history.slice(0, -1) }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to get response');
      }
      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response stream');
      const decoder = new TextDecoder();
      let assistantContent = '';
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.type === 'suggestions') {
              setSuggestions(parsed.suggestions || []);
              continue;
            }
            if (parsed.choices?.[0]?.delta?.content) {
              setIsThinking(false);
              assistantContent += parsed.choices[0].delta.content;
              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { role: 'assistant', content: assistantContent };
                return updated;
              });
            }
          } catch {
            /* skip */
          }
        }
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again or contact postmaster@edj-labs.com for assistance.' },
      ]);
    } finally {
      setIsLoading(false);
      setIsThinking(false);
    }
  }, [isLoading, messages, keycloak?.token]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleNewChat = () => {
    setMessages([]);
    setSuggestions([]);
    fetchSuggestions();
  };

  if ((!aiEnabled || !isEnabled('api-ai')) && !isOpen) return null;

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={cn(
            'fixed bottom-5 right-5 z-40 inline-flex h-12 w-12 items-center justify-center rounded-full',
            'bg-primary text-primary-foreground shadow-elevation-3 transition-all hover:scale-105 hover:bg-primary/90',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'
          )}
          aria-label="Open AI Assistant"
        >
          <SparklesIcon className="h-5 w-5" />
        </button>
      )}

      {isOpen && (
        <div
          className={cn(
            'fixed bottom-4 right-4 z-40 flex w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-elevation-3',
            'h-[min(32rem,calc(100vh-6rem))] animate-fade-in-up sm:bottom-5 sm:right-5'
          )}
        >
          <div className="flex items-center justify-between border-b border-border bg-card px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                <SparklesIcon className="h-4 w-4" />
              </span>
              <div className="flex flex-col leading-tight">
                <span className="text-sm font-semibold text-foreground">EDJ-LABS Assistant</span>
                <span className="text-[10px] text-muted-foreground">Beta</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleNewChat}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                title="New chat"
                aria-label="New chat"
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                aria-label="Close chat"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4 bg-background/30">
            {!aiEnabled && (
              <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
                <ChatBubbleLeftRightIcon className="h-6 w-6" />
                AI features are currently disabled.
              </div>
            )}

            {aiEnabled && messages.length === 0 && (
              <div className="flex flex-col items-center gap-4 py-4 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <SparklesIcon className="h-6 w-6" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">Hi! How can I help?</p>
                  <p className="text-xs text-muted-foreground">Ask about your services, domains, or bills.</p>
                </div>
                {suggestions.length > 0 && (
                  <div className="w-full space-y-2">
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(s)}
                        className="block w-full rounded-lg border border-border bg-card px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-primary/40 hover:bg-accent"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={cn('flex', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : 'bg-card border border-border text-foreground rounded-bl-sm'
                  )}
                >
                  {msg.content ? (
                    msg.role === 'assistant' ? renderMessageContent(msg.content, navigate) : msg.content
                  ) : isThinking ? (
                    <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs">
                      <svg className="h-3.5 w-3.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      </svg>
                      Thinking...
                    </span>
                  ) : (
                    <span className="inline-flex gap-1 text-muted-foreground">
                      <span className="animate-pulse">●</span>
                      <span className="animate-pulse" style={{ animationDelay: '0.2s' }}>●</span>
                      <span className="animate-pulse" style={{ animationDelay: '0.4s' }}>●</span>
                    </span>
                  )}
                </div>
              </div>
            ))}

            {!isLoading && messages.length > 0 && messages[messages.length - 1].role === 'assistant' && suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(s)}
                    className="rounded-full border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            <div ref={endRef} />
          </div>

          <form onSubmit={handleSubmit} className="border-t border-border bg-card px-3 py-2.5">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={aiEnabled ? 'Ask anything…' : 'AI features disabled'}
                disabled={!aiEnabled || isLoading}
                className={cn(
                  'flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground',
                  'placeholder:text-muted-foreground',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  'disabled:cursor-not-allowed disabled:opacity-60'
                )}
              />
              <button
                type="submit"
                disabled={!aiEnabled || isLoading || !input.trim()}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Send message"
              >
                <PaperAirplaneIcon className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
